import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import InstallPrompt from "../components/InstallPrompt";
import { Smartphone, Monitor, Apple, ArrowLeft, Sparkles } from "lucide-react";

export default function InstallHelp() {
  const [platform, setPlatform] = useState("unknown");

  useEffect(() => {
    const ua = navigator.userAgent;
    const isStandalone =
      window.navigator.standalone === true ||
      window.matchMedia("(display-mode: standalone)").matches;
    if (isStandalone) { setPlatform("installed"); return; }
    const isIOS = /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
    const isIPadOS = /Mac/.test(ua) && navigator.maxTouchPoints && navigator.maxTouchPoints > 1;
    if (isIOS || isIPadOS) { setPlatform("ios"); return; }
    if (/android/i.test(ua)) { setPlatform("android"); return; }
    const isEdge = /Edg\//.test(ua);
    const isChrome = /Chrome\//.test(ua) && !isEdge && !/Chromium/.test(ua);
    const isSafariMac = /^((?!chrome|android|edg).)*safari/i.test(ua) && /Mac OS X/.test(ua);
    if (isEdge) { setPlatform("desktop-edge"); return; }
    if (isChrome) { setPlatform("desktop-chrome"); return; }
    if (isSafariMac) { setPlatform("mac-safari"); return; }
    setPlatform("desktop-other");
  }, []);

  const benefits = [
    { label: "One-tap launch from your home screen / dock" },
    { label: "Full-screen — no browser address bar wasting space" },
    { label: "Push notifications for Monday Mission and challenges" },
    { label: "Works offline for the screens you've already viewed" },
    { label: "Safe-area + native-feel UI on iPhone/iPad" },
  ];

  const cards = {
    ios: {
      icon: Apple,
      title: "Install on iPhone or iPad",
      steps: [
        "Open this page in Safari (not Chrome — Apple only allows installs from Safari).",
        "Tap the Share button (square with up arrow) in the bottom toolbar.",
        'Scroll and tap "Add to Home Screen".',
        'Tap "Add" — the GYM with GRIZZ icon appears on your home screen.',
      ],
    },
    android: {
      icon: Smartphone,
      title: "Install on Android",
      steps: [
        "Open this page in Chrome.",
        "If you see the install banner above, tap Install — done.",
        'Otherwise: open the browser menu (⋮ top-right) → "Install app" or "Add to Home screen".',
        "Confirm. The app icon will appear in your launcher.",
      ],
    },
    "desktop-chrome": {
      icon: Monitor,
      title: "Install on Windows / Mac (Chrome)",
      steps: [
        'Look for the install icon (⊕ or "+" inside a square) on the right side of the address bar.',
        'Click it, then click "Install" in the popup.',
        "GYM with GRIZZ opens in its own window — pin to taskbar (Windows) or dock (Mac).",
      ],
    },
    "desktop-edge": {
      icon: Monitor,
      title: "Install on Windows / Mac (Microsoft Edge)",
      steps: [
        'Look for the install icon (⊕) on the right side of the address bar.',
        'Or open Settings (⋯) → "Apps" → "Install this site as an app".',
        'Click "Install".',
      ],
    },
    "mac-safari": {
      icon: Apple,
      title: "Install on Mac (Safari 17+)",
      steps: [
        "On macOS Sonoma or newer, in the Safari menu bar:",
        'Click "File" → "Add to Dock…"',
        'Pick a name and click "Add". The app appears in your Dock.',
      ],
    },
    "desktop-other": {
      icon: Monitor,
      title: "Install on Desktop",
      steps: [
        "For the best install experience, open this site in Google Chrome or Microsoft Edge.",
        "Firefox doesn't support PWA installs natively — but you can still bookmark this site for one-click access.",
      ],
    },
    installed: {
      icon: Sparkles,
      title: "You're already installed!",
      steps: ["GYM with GRIZZ is running as an installed app right now. Crush some PRs."],
    },
  };

  const card = cards[platform] || cards["desktop-other"];
  const Icon = card.icon;

  return (
    <div className="max-w-3xl mx-auto p-4 sm:p-6">
      <Link to="/dashboard" data-testid="install-back-link" className="flex items-center text-neutral-400 hover:text-blue-500 text-xs uppercase tracking-widest mb-4">
        <ArrowLeft className="w-4 h-4 mr-1" /> Back
      </Link>

      <div className="text-xs tracking-[0.3em] uppercase text-blue-500 font-bold mb-1">PWA INSTALL</div>
      <h1 className="font-display text-4xl sm:text-5xl uppercase leading-none mb-2">Get the App</h1>
      <p className="text-sm text-neutral-400 mb-5">
        GYM with GRIZZ is a Progressive Web App — same experience as a native app, no app store needed. Install once on each device, train forever.
      </p>

      <InstallPrompt mode="banner" className="mb-5" />

      <section className="border border-neutral-800 mb-5">
        <div className="p-5 bg-gradient-to-br from-blue-500/10 to-transparent border-b border-neutral-800">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-blue-500 flex items-center justify-center"><Icon className="w-5 h-5 text-white" /></div>
            <h2 data-testid="install-platform-title" className="font-display text-2xl uppercase">{card.title}</h2>
          </div>
        </div>
        <div className="p-5">
          <ol data-testid="install-steps" className="space-y-3 text-sm text-neutral-200">
            {card.steps.map((s, i) => (
              <li key={i} className="flex gap-3">
                <span className="font-display text-blue-500 shrink-0">{platform === "installed" ? "✓" : i + 1}</span>
                <span>{s}</span>
              </li>
            ))}
          </ol>
        </div>
      </section>

      <section data-testid="install-benefits" className="border border-neutral-800 mb-5">
        <div className="p-5 border-b border-neutral-800">
          <h3 className="font-display text-xl uppercase">Why Install</h3>
        </div>
        <ul className="p-5 space-y-2 text-sm text-neutral-300">
          {benefits.map((b, i) => (
            <li key={i} className="flex gap-3">
              <span className="text-blue-500 shrink-0">▸</span>
              <span>{b.label}</span>
            </li>
          ))}
        </ul>
      </section>

      <section className="border border-neutral-800 mb-5 p-5">
        <h3 className="font-display text-xl uppercase mb-2">Other devices</h3>
        <p className="text-xs text-neutral-400 mb-3">Want to install on a different device? Here are the steps for each:</p>
        <div className="grid sm:grid-cols-2 gap-2">
          {["ios", "android", "desktop-chrome", "desktop-edge", "mac-safari"].filter((k) => k !== platform).map((k) => {
            const c = cards[k];
            const ic = c.icon;
            return (
              <button
                key={k}
                data-testid={`switch-platform-${k}`}
                onClick={() => setPlatform(k)}
                className="flex items-center gap-3 p-3 border border-neutral-800 hover:border-blue-500/60 text-left"
              >
                <div className="w-8 h-8 bg-neutral-900 border border-neutral-800 flex items-center justify-center"><ic className="w-4 h-4 text-blue-500" /></div>
                <div className="flex-1 min-w-0">
                  <div className="font-display text-sm uppercase truncate">{c.title}</div>
                </div>
              </button>
            );
          })}
        </div>
      </section>
    </div>
  );
}
